import { captureIfInitialised } from '@myosh/sentry/capture';
import api, { ApiError } from '../services/api';
import { DEFAULT_IDP } from './constants';

/**
 * Resolves the Cognito IdP for a username. A failed lookup also falls back to the
 * default, but is reported.
 * @param email
 * @returns the lowercased domain, or {@link DEFAULT_IDP}
 */

export const checkIdp = async (email: string) => {
  // Domain names are case-insensitive, but Cognito matches the IdP identifier
  // as an exact-match (case-sensitive) string. Canonicalise to lowercase so the
  // domain is recognised on both the hasCognitoIdentityProvider check and the
  // idp_identifier reused for the Cognito redirect.
  const trimmed = email.trim();
  const atIndex = trimmed.lastIndexOf('@');
  const domain =
    atIndex === -1
      ? ''
      : trimmed
          .slice(atIndex + 1)
          .trim()
          .toLowerCase();

  // Returning here also keeps raw input out of telemetry: with no `@` the
  // extraction would yield the whole string.
  if (!domain) {
    return DEFAULT_IDP;
  }

  try {
    // URLSearchParams so a value carrying `&` or `=` cannot alter the query.
    const query = new URLSearchParams({ domain, username: trimmed }).toString();
    const response = await api<boolean>(`hasCognitoIdentityProvider?${query}`);

    // A non-boolean body is a broken contract, not an answer — same fallback,
    // but don't let it pass as a definitive "no IdP".
    if (typeof response !== 'boolean') {
      captureIfInitialised(new Error('IdP lookup returned a non-boolean body'), {
        level: 'error',
        tags: { auth_check: 'has-cognito-identity-provider', failure_kind: 'invalid-body' },
        extra: { domain, bodyType: typeof response },
      });
      return DEFAULT_IDP;
    }

    return response ? domain : DEFAULT_IDP;
  } catch (error) {
    // Keep the fallback so an unreachable API can't block login, but report it:
    // silently defaulting sends SSO users to the wrong provider.
    captureIfInitialised(error, {
      level: 'error',
      tags: {
        auth_check: 'has-cognito-identity-provider',
        failure_kind: error instanceof ApiError ? error.kind : 'unknown',
      },
      extra: {
        domain,
        ...(error instanceof ApiError && error.status !== undefined ? { status: error.status } : {}),
      },
    });
    return DEFAULT_IDP;
  }
};
